import { FormHandles, Scope } from '@unform/core';
import { Form } from '@unform/web';
import { Alert, HiddenInput, Input, Modal } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchAdminActions as FetchActions,
  UpdateAdminActions as UpdateActions,
} from 'store/ducks/admins';
import { DeleteAdminActions as DeleteActions } from 'store/ducks/admins';
import { UpdateAdminValidator } from 'validators/Admins';
import * as S from './styles';

interface Props {
  adminId: string | number;
  onUpdate?: () => void;
}

export const AdminUpdateForm: React.FC<Props> = ({ adminId, onUpdate }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { data: admin } = useSelector((state: RootState) => state.fetchAdmin);

  const { loading: updatingAdmin, validationErrors } = useSelector(
    (state: RootState) => state.updateAdmin
  );

  const { loading: deletingAdmin } = useSelector(
    (state: RootState) => state.deleteAdmin
  );

  const fetchAdmin = useCallback((): void => {
    dispatch(FetchActions.request(adminId));
  }, [dispatch, adminId]);

  const onAdminLoad = useCallback((): void => {
    if (!admin) return;

    const { user, userId } = admin;

    formRef.current?.setFieldValue('userId', userId);
    formRef.current?.setFieldValue('user.name', user.name);
    formRef.current?.setFieldValue('user.email', user.email);
  }, [admin]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateAdminValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(adminId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [adminId, dispatch, handleFormErrors, onSuccess]
  );

  const onDelete = useCallback(() => {
    setModalOpen(false);
    dispatch(DeleteActions.request(adminId, onSuccess));
  }, [dispatch, handleFormErrors, adminId]);

  useEffect(() => {
    fetchAdmin();
  }, [fetchAdmin]);

  useEffect(() => {
    onAdminLoad();
  }, [onAdminLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <HiddenInput name="userId" />
        <Scope path="user">
          <S.FormRow>
            <Input name="name" label="Nome completo" />
            <Input name="email" label="Email" />
            <Input name="password" label="Senha" type="password" />
          </S.FormRow>
        </Scope>
        <Modal isOpen={modalOpen} onClickOutside={() => setModalOpen(false)}>
          <Alert
            title={'Excluir Usuário'}
            text={'Deseja realmente excluir este usuário?'}
            close={() => setModalOpen(false)}
            action={onDelete}
            labelAction={'Sim'}
          />
        </Modal>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/administradores">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit" disabled={updatingAdmin}>
            {updatingAdmin ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
          <S.Button
            type="button"
            mood="danger"
            onClick={() => setModalOpen(true)}
            disabled={deletingAdmin}
          >
            {deletingAdmin ? <S.ActivityIndicator /> : 'Excluir'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
